import {Category} from '../definition/category';
import {EventEmitter} from '@angular/core';
import {IDefinition} from '../definition/definition';
import {Question} from "../definition/question";
import {ObjectUtil} from "../../utils";
import {ICategoryData} from "../definition/category-data";
import {IAttachment} from "../project/project-data";


/**
 * New entity that replaces the company type
 */
export interface ProjectType {
    id: number;
    name: string;
    description?: string;
}

export enum ProjectAnalysisDefinitionStatus {
    ACTIVE = 'ACTIVE',
    CONCEPT = 'CONCEPT',
    ARCHIVED = 'ARCHIVED'
}

/**
 *  Response of endpoint `/api/project-types`.
 */
export type ProjectTypeListResponse = ProjectType[];

/**
 * Shared status handling for project analysis models.
 */
export abstract class ProjectAnalysisStatusModel {
    public status: ProjectAnalysisDefinitionStatus;
    public publishedAt: Date;

    protected constructor(
        publishedAt: string | Date | null,
        status?: ProjectAnalysisDefinitionStatus,
        isPublishedFallback?: boolean
    ) {
        this.publishedAt = publishedAt == null ? null : new Date(publishedAt);
        this.status = status ?? ((isPublishedFallback ?? this.publishedAt !== null)
            ? ProjectAnalysisDefinitionStatus.ACTIVE
            : ProjectAnalysisDefinitionStatus.CONCEPT);
    }

    get isActive(): boolean {
        return this.status === ProjectAnalysisDefinitionStatus.ACTIVE;
    }

    get isConcept(): boolean {
        return this.status === ProjectAnalysisDefinitionStatus.CONCEPT;
    }

    get isArchived(): boolean {
        return this.status === ProjectAnalysisDefinitionStatus.ARCHIVED;
    }

    /** @deprecated use `isActive` / `status` instead. Kept for legacy templates. */
    get isPublished(): boolean {
        return this.isActive;
    }
}

/**
 * Project analysis definition list item, used in the version overview page.
 */
export class ProjectAnalysisVersion extends ProjectAnalysisStatusModel {
    public id: number;
    public versionId: number;
    public projectType: ProjectType;

    constructor(item: ProjectAnalysisVersionItem) {
        super(item.publishedAt, item.status);
        this.id = item.id;
        this.versionId = item.versionId;
        this.projectType = item.projectType;
    }
}

/**
 *  Response of endpoint `/api/project-analysis/{projectTypeId}/definitions/versions`.
 */
export type ProjectAnalysisVersionsResponse = ProjectAnalysisVersionItem[];

/**
 *  List item of `versions` call, currently not all fields used, might be removed if not needed.
 */
export interface ProjectAnalysisVersionItem {
    id: number;
    versionId: number;
    projectType: ProjectType;
    createdAt: string;
    publishedAt: string;
    redFlagTresholdCategory: number;
    // TODO BE: make required once DefinitionVersionDto exposes the status enum
    //  (the detail DTO already does).
    status?: ProjectAnalysisDefinitionStatus;
}

/**
 *  Response of endpoint `/api/project-analysis/{projectTypeId}/definitions/{version}`.
 */
export interface ProjectAnalysisDefinitionResponse {
    id: number;
    versionId: number;
    projectType: ProjectType;
    createdAt: Date;
    isPublished: boolean;
    publishedAt: Date;
    categories: ICategoryData[];
    redFlagTresholdCategory: number;
    status: ProjectAnalysisDefinitionStatus;
}

/**
 * Composed interface for project analysis definitions, containing some TIS and Quickscan logic.
 */
export class ProjectAnalysisDefinition extends ProjectAnalysisStatusModel implements IDefinition {
    public id: number;
    public versionId: number;
    public createdAt: Date;
    public redFlagTresholdCategory: number;
    public categories: Category[] = [];
    public onChange: EventEmitter<IDefinition>;
    public projectType: ProjectType;

    constructor(data: ProjectAnalysisDefinitionResponse) {
        super(data.publishedAt, data.status, data.isPublished);
        this.id = data.id;
        this.versionId = data.versionId;
        this.createdAt = new Date(data.createdAt);
        this.redFlagTresholdCategory = data.redFlagTresholdCategory ?? 0;
        this.categories = data.categories.map((category) => new Category(category, this));
        this.projectType = data.projectType;
        this.onChange = new EventEmitter();
    }

    public get questions(): Question[] {
        return this.categories.flatMap(c => c.questions);
    }

    public getDefinition(): IDefinition {
        return this;
    }

    public cloneData(): ProjectAnalysisDefinition {
        return ObjectUtil.cloneObject(this);
    }

    public broadcastChanges(): void {
        this.onChange.emit(this.getDefinition());
    }

    public getCategories(): Category[] {
        return this.categories;
    }

    public addCategory(data: ICategoryData): Category {
        const newCategory = new Category(data, this);
        this.categories.push(newCategory);
        this.broadcastChanges();

        return newCategory;
    }

    /**
     * We do not use subcategories in project analysis, but we need this logic when moving categories and questions.
     */
    public getSubCategories(): Category[] {
        const subCategories = [];

        this.categories.forEach((category) => {
            category.subCategories.forEach((subCategory) => {
                subCategories.push(subCategory);
            });
        });

        return subCategories;
    }

    public deleteCategory(category: Category): void {
        this.categories = this.categories.filter((theme) => theme !== category);
        this.broadcastChanges();
    }

    public moveCategoryUp(category: Category) {
        const index = ObjectUtil.findIndexForEqualObject(category, this.categories);
        const newIndex = Math.max(0, index - 1);

        this.swapCategories(index, newIndex);
    }

    public moveCategoryDown(category: Category) {
        const index = ObjectUtil.findIndexForEqualObject(category, this.categories);
        const newIndex = Math.min(this.categories.length - 1, index + 1);

        this.swapCategories(index, newIndex);
    }

    private swapCategories(index1: number, index2: number) {
        const item1 = this.categories[index1];
        const item2 = this.categories[index2];

        this.categories[index1] = item2;
        this.categories[index2] = item1;
    }
}

export interface ProjectAnalysisAnswer {
    id?: number;
    questionId: number;
    answerAsText?: string;
    answerAsDate?: string;
    answerAsInteger?: number;
    answerAsBool?: boolean;
    answerAsPercentage?: number;
    attachments: IAttachment[];
    colorScore?: string;
    explanation?: string;
    // in case the answer is pre-defined;
    answerId?: number;
    pitTag?: string;
}

/**
 * A link to a project, where `link` should be an URL, like `mailto:example@example.com` or `http://example.com`.
 *
 * <p>Endpoints:
 * <ul>
 *     <li>List - `GET /api/projects/{projectId}/links`
 *     <li>Create -`POST /api/projects/{projectId}/links`
 *     <li>Update -`POST /api/projects/{projectId}/links/{linkId}`
 *     <li>Delete -`DELETE /api/projects/{projectId}/links/{linkId}`
 * </ul>
 */
export interface ProjectLink {
    id?: number;
    //@NotNull @NotBlank @Max(255)
    title: string;
    //@NotNull @NotBlank @URL @Max(1024)
    link: string;
    // optional field, not sure if needed.
    description?: string;
    // readonly
    createdAt?: string;
    // readonly/nullable
    modifiedAt?: string;
}
